import * as React from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type Tone = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

const toneClasses: Record<Tone, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
  info: 'text-info',
  muted: 'text-muted-foreground',
};

export type KpiCardProps = {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  hint?: React.ReactNode;
  tone?: Tone;
  icon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
  /** Avancement vers l'objectif (0-100). Affiche une barre fine sous la valeur. */
  progress?: number;
  progressClassName?: string;
};

const sizeClasses = {
  sm: { value: 'text-xl', label: 'text-xs', sub: 'text-xs', pad: 'p-3' },
  md: { value: 'text-2xl', label: 'text-xs', sub: 'text-xs', pad: 'p-4' },
  lg: { value: 'text-3xl md:text-4xl', label: 'text-sm', sub: 'text-sm', pad: 'p-4 md:p-5' },
  xl: { value: 'text-4xl md:text-5xl', label: 'text-sm', sub: 'text-sm', pad: 'p-5 md:p-6' },
};

export function KpiCard({ label, value, sub, hint, tone = 'default', icon, size = 'md', className, onClick, progress, progressClassName }: KpiCardProps) {
  const s = sizeClasses[size];
  return (
    <Card
      onClick={onClick}
      className={cn('overflow-hidden transition-shadow hover:shadow-md', onClick && 'cursor-pointer', className)}
    >
      <CardContent className={cn('flex flex-col gap-1.5', s.pad)}>
        <div className={cn('flex items-center gap-1.5 font-medium text-muted-foreground uppercase tracking-wide', s.label)}>
          {icon}
          <span>{label}</span>
          {hint && (
            <Tooltip>
              <TooltipTrigger className="ml-auto inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground">
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{hint}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className={cn('font-bold leading-tight num', s.value, toneClasses[tone])}>{value}</div>
        {sub && <div className={cn('text-muted-foreground num', s.sub)}>{sub}</div>}
        {progress !== undefined && (
          <Progress
            value={Math.max(0, Math.min(100, progress))}
            className="mt-0.5 h-1"
            indicatorClassName={progressClassName}
          />
        )}
      </CardContent>
    </Card>
  );
}
